import { useId, type ReactNode } from 'react';
import { cn } from '../cn.js';
import { useDrawIn } from './internal/useDrawIn.js';

export interface RingProps {
  /** 0–100. Values outside that range are clamped rather than producing an invalid stroke offset. */
  value: number;
  size?: number;
  strokeWidth?: number;
  /** A token reference, e.g. "var(--accent)" — never a literal colour. */
  color?: string;
  label?: ReactNode;
  valueText?: ReactNode;
  sublabel?: ReactNode;
  /** The sublabel's tone — `success` (the default) reads as progress made; `muted` as plain info. */
  sublabelTone?: 'success' | 'muted';
  /**
   * Clock-face tick marks inside the track. Defaults on for large rings (≥ 120px), where the ring
   * is the card's centrepiece; off below that, where ticks would only be noise.
   */
  ticks?: boolean;
  className?: string;
}

/**
 * Circular progress — the working-time ring on the attendance home screen, and anywhere else a
 * single 0–100 figure is the point (a leave-quota dial, a course-completion ring).
 *
 * The arc is a soft gradient of `color` with a faint glow, and large rings add a clock face and a
 * head marker at the arc's leading end. The gradient's id comes from `useId`, so two rings on one
 * page never fight over the same `<linearGradient id>`.
 *
 * Draws in from empty the first time it scrolls into view (`useDrawIn`, same "reveal once, not a
 * replay-every-scroll scrubber" mechanism `BarChart`/`DonutChart` use) rather than snapping straight
 * to `value` on mount, so a ring below the fold still reads as a deliberate reveal instead of static,
 * already-finished chrome. The head marker rides the same transition.
 */
const DRAW_IN_DURATION = '900ms';
const DRAW_IN_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

export function Ring({
  value,
  size = 160,
  strokeWidth = 13,
  color = 'var(--accent)',
  label,
  valueText,
  sublabel,
  sublabelTone = 'success',
  ticks,
  className,
}: RingProps) {
  const [drawInRef, revealed] = useDrawIn<HTMLDivElement>();
  const gradientId = `ring-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const clamped = Math.min(100, Math.max(0, value));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);
  const center = size / 2;
  const isLarge = size >= 120;
  const showTicks = ticks ?? isLarge;
  const showHead = isLarge && clamped > 0 && clamped < 100;
  const transition = `${DRAW_IN_DURATION} ${DRAW_IN_EASE}`;

  // Tick ring: sits just inside the track. 60 fine minute ticks plus 12 heavier hour ticks, each
  // drawn as one dashed circle — a dash every `circumference / n`, offset by half a dash so the
  // first one lands centred on 12 o'clock.
  const tickRadius = radius - strokeWidth / 2 - 5;
  const tickCircumference = 2 * Math.PI * tickRadius;
  const minorDash = 1;
  const majorDash = 2;

  // The value text was a fixed `text-2xl` regardless of `size` — fine at the 160px default this
  // component was first built for, but a card-sized 64px ring (e.g. an Objective card's rollup)
  // rendered the same 24px glyphs, which are wider than the ring's own inner diameter and spill
  // past the stroke. Three discrete tiers rather than a continuous formula: legible digit sizes
  // don't scale linearly with a circle's diameter, and picking from real Tailwind steps keeps
  // rendering crisp instead of landing on an arbitrary sub-pixel size.
  const isSmall = size < 90;
  const isMedium = size >= 90 && size < 140;
  // `text-sm`, not `text-base`, for the small tier: a 100% reading is 4 characters, and the inner
  // maxWidth at a 64px ring (this component's smallest common usage) is under 40px — `text-base`
  // fits "23%" but clips "100%" against the stroke.
  const valueTextClass = isSmall ? 'text-sm' : isMedium ? 'text-lg' : 'text-2xl';
  const labelClass = isSmall ? 'text-[9px]' : 'text-[10px]';
  const sublabelClass = isSmall ? 'text-[10px]' : 'text-xs';
  const stackGapClass = isSmall ? 'gap-0' : isLarge ? 'gap-1.5' : 'gap-0.5';
  // Ticks take a band inside the track, so the text gets that much less room.
  const innerInset = strokeWidth * 2 + (showTicks ? 18 : 12);

  return (
    <div
      ref={drawInRef}
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        className="-rotate-90 overflow-visible"
        aria-hidden="true"
      >
        <defs>
          {/* The SVG is turned -90°, so this runs from the visual top-right (light, where every
              arc starts) to the bottom-left (full colour, where a well-filled arc's head lands). */}
          <linearGradient id={gradientId} x1="1" y1="1" x2="0" y2="0">
            <stop
              offset="0"
              style={{ stopColor: `color-mix(in oklab, ${color} 55%, var(--surface))` }}
            />
            <stop offset="1" style={{ stopColor: color }} />
          </linearGradient>
        </defs>

        {/* Track — a quiet groove the arc runs in. */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--bg-subtle)"
          strokeWidth={strokeWidth}
        />
        {isLarge && (
          <circle
            cx={center}
            cy={center}
            r={radius + strokeWidth / 2 - 0.5}
            fill="none"
            stroke="var(--border)"
            strokeOpacity="0.7"
          />
        )}

        {showTicks && (
          <>
            <circle
              cx={center}
              cy={center}
              r={tickRadius}
              fill="none"
              stroke="var(--border-strong)"
              strokeOpacity="0.55"
              strokeWidth={3}
              strokeDasharray={`${minorDash} ${tickCircumference / 60 - minorDash}`}
              strokeDashoffset={minorDash / 2}
            />
            <circle
              cx={center}
              cy={center}
              r={tickRadius - 1}
              fill="none"
              stroke="var(--fg-subtle)"
              strokeWidth={5}
              strokeDasharray={`${majorDash} ${(2 * Math.PI * (tickRadius - 1)) / 12 - majorDash}`}
              strokeDashoffset={majorDash / 2}
            />
          </>
        )}

        {/* Progress arc. */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={revealed ? offset : circumference}
          style={{
            transition: `stroke-dashoffset ${transition}`,
            filter: isLarge
              ? `drop-shadow(0 0 ${strokeWidth * 0.6}px color-mix(in oklab, ${color} 35%, transparent))`
              : undefined,
          }}
        />

        {/* Head marker at the arc's leading end — rotated into place, so it follows the draw-in. */}
        {showHead && (
          <g
            style={{
              transform: `rotate(${revealed ? (clamped / 100) * 360 : 0}deg)`,
              transformOrigin: `${center}px ${center}px`,
              transition: `transform ${transition}`,
            }}
          >
            <circle
              cx={center + radius}
              cy={center}
              r={strokeWidth / 2 + 1.5}
              fill="var(--surface)"
              stroke={color}
              strokeWidth={2.5}
            />
          </g>
        )}
      </svg>
      {/*
       * Constrained to the ring's INNER diameter, not the full box. Without this the label sits in
       * a box as wide as the ring's outer edge, so a longer sublabel ("↑ 74% of daily goal") runs
       * under the stroke instead of wrapping inside it — visible immediately at smaller sizes.
       */}
      <div
        className={cn(
          'absolute inset-0 mx-auto flex flex-col items-center justify-center text-center',
          stackGapClass,
        )}
        style={{ maxWidth: Math.max(0, size - innerInset) }}
      >
        {label && (
          <span
            className={cn(labelClass, 'font-semibold uppercase tracking-[0.12em] text-fg-subtle')}
          >
            {label}
          </span>
        )}
        {valueText && (
          <span
            className={cn(
              valueTextClass,
              'font-display font-bold tabular-nums tracking-tight text-fg leading-none',
            )}
          >
            {valueText}
          </span>
        )}
        {sublabel && (
          <span
            className={cn(
              sublabelClass,
              'font-semibold',
              sublabelTone === 'muted' ? 'text-fg-muted' : 'text-success',
            )}
          >
            {sublabel}
          </span>
        )}
      </div>
    </div>
  );
}
